/**
 * F-604-FE-03: NotificationList
 *
 * Dropdown panel listing in-app notifications.
 * - Type-specific icons: checkmark (NOTE_READY), X (NOTE_FAILED),
 *   document (PDF_READY), clock (DRAFT_REMINDER)
 * - Message text and relative timestamps
 * - Unread items highlighted with a subtle left border
 * - Clicking an item performs type-specific action and marks as read
 *
 * Type-specific actions:
 *   NOTE_READY    → navigate to /notes/:noteId (falls back to /notes)
 *   NOTE_FAILED   → navigate to /notes (transcript view for that session)
 *   PDF_READY     → trigger download via window.open(downloadUrl)
 *   DRAFT_REMINDER → navigate to /notes/:noteId
 */

import { useNavigate } from 'react-router-dom';
import { useNotifications, type NotificationItem, type NotificationType } from '@/hooks/useNotifications';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function relativeTime(isoString: string): string {
  const now = Date.now();
  const then = new Date(isoString).getTime();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(isoString).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

// ---------------------------------------------------------------------------
// Type-specific icons
// ---------------------------------------------------------------------------

function NoteReadyIcon() {
  return (
    <svg className="w-5 h-5 text-clinical-teal flex-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function NoteFailedIcon() {
  return (
    <svg className="w-5 h-5 text-red-500 flex-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function PdfReadyIcon() {
  return (
    <svg className="w-5 h-5 text-clinical-charcoal flex-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  );
}

function DraftReminderIcon() {
  return (
    <svg className="w-5 h-5 text-clinical-amber flex-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function NotificationIcon({ type }: { type: NotificationType }) {
  switch (type) {
    case 'NOTE_READY':     return <NoteReadyIcon />;
    case 'NOTE_FAILED':    return <NoteFailedIcon />;
    case 'PDF_READY':      return <PdfReadyIcon />;
    case 'DRAFT_REMINDER': return <DraftReminderIcon />;
    default:               return <NoteReadyIcon />;
  }
}

// ---------------------------------------------------------------------------
// Single notification row
// ---------------------------------------------------------------------------

interface NotificationRowProps {
  notification: NotificationItem;
  onAction: (notification: NotificationItem) => void;
}

function NotificationRow({ notification, onAction }: NotificationRowProps) {
  return (
    <button
      type="button"
      className={`
        w-full text-start
        flex items-start gap-3 px-4 py-3
        hover:bg-clinical-cream
        transition-colors duration-150
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-clinical-amber
        ${!notification.read ? 'border-s-2 border-clinical-teal bg-clinical-cream/40' : ''}
      `}
      onClick={() => onAction(notification)}
      aria-label={notification.message}
    >
      {/* Type icon */}
      <div className="mt-0.5">
        <NotificationIcon type={notification.type} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-body leading-snug text-clinical-charcoal ${!notification.read ? 'font-semibold' : ''}`}>
          {notification.message}
        </p>
        <p className="text-xs font-body text-clinical-text-muted mt-0.5">
          {relativeTime(notification.createdAt)}
        </p>
      </div>

      {/* Unread dot */}
      {!notification.read && (
        <span className="mt-1.5 flex-none w-2 h-2 rounded-full bg-clinical-teal" aria-hidden="true" />
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface NotificationListProps {
  onClose: () => void;
}

export function NotificationList({ onClose }: NotificationListProps) {
  const navigate = useNavigate();
  const { notifications, markAsRead, isLoading } = useNotifications();

  function handleAction(notification: NotificationItem) {
    // Mark as read first (optimistic)
    markAsRead(notification.id);
    onClose();

    const { type, metadata } = notification;

    switch (type) {
      case 'NOTE_READY': {
        const noteId = metadata?.noteId as string | undefined;
        if (noteId) {
          navigate(`/notes/${noteId}`);
        } else {
          navigate('/notes');
        }
        break;
      }
      case 'NOTE_FAILED': {
        // Navigate to notes list — doctor can review transcript from there
        navigate('/notes');
        break;
      }
      case 'PDF_READY': {
        const downloadUrl = metadata?.downloadUrl as string | undefined;
        if (downloadUrl) {
          window.open(downloadUrl, '_blank', 'noopener,noreferrer');
        }
        break;
      }
      case 'DRAFT_REMINDER': {
        const noteId = metadata?.noteId as string | undefined;
        if (noteId) {
          navigate(`/notes/${noteId}`);
        } else {
          navigate('/notes');
        }
        break;
      }
      default:
        navigate('/notes');
    }
  }

  return (
    <div
      className="
        absolute end-0 top-full mt-1 z-50
        w-80 max-w-[calc(100vw-2rem)]
        bg-white rounded-clinical shadow-clinical-md border border-clinical-border
        overflow-hidden
      "
      role="dialog"
      aria-label="Notifications"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-clinical-border">
        <h2 className="text-sm font-heading font-semibold text-clinical-charcoal">
          Notifications
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="
            inline-flex items-center justify-center
            w-8 h-8 rounded-lg
            text-clinical-text-muted hover:text-clinical-charcoal hover:bg-clinical-cream
            transition-colors duration-150
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clinical-amber
          "
          aria-label="Close notifications"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Body */}
      <div className="max-h-[420px] overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-5 h-5 border-2 border-clinical-teal border-t-transparent rounded-full animate-spin" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
            <svg className="w-10 h-10 text-clinical-border mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
            </svg>
            <p className="text-sm font-body text-clinical-text-muted">
              No new notifications
            </p>
          </div>
        ) : (
          <ul role="list" className="divide-y divide-clinical-border/60">
            {notifications.map((notification) => (
              <li key={notification.id}>
                <NotificationRow
                  notification={notification}
                  onAction={handleAction}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
